import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SnackBarService } from '@shared/services/snack-bar.service';
import { catchError, EMPTY, map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GENERAL_ERROR_MESSAGE } from '@shared/models/messages';
import { WatchlistProject } from '../models/watchlist-project.model';
import {
  CountrySectorWatchlistTableContentDto,
  FlattenedCountrySector,
  WatchListCountrySectorFlattenedResponse,
  WatchlistCountrySectorResponse,
} from '../models/watchlist-country-sector.model';
import { transformSensitivity } from '@shared/utils/sensitivity-transform.util';
import { transformDateString } from '@shared/utils/date-string-transform.utils';
import { WatchlistCompanyResponse } from '../models/watchlist-company.model';
import { parseErrorMessageFromBlob } from '@shared/utils/blob-error-message-parse.utils';

@Injectable()
export class WatchlistsApiService {
  readonly http = inject(HttpClient);
  private _messageService = inject(SnackBarService);
  readonly url = `${environment.apiUrl}`;

  getWatchlistsCompany$(): Observable<WatchlistCompanyResponse> {
    return this.http.get<WatchlistCompanyResponse>(this.url + '/v2/company-watchlist').pipe(
      map(
        response =>
          ({
            industrySegmentHeaders: response.industrySegmentHeaders,
            companyWatchlistTableContent: response.companyWatchlistTableContent.map(company => ({
              ...company,
              updatedDate: transformDateString(company.updatedDate),
              validFrom: transformDateString(company.validFrom),
            })),
          }) as WatchlistCompanyResponse
      ),
      catchError(error => {
        this._messageService.showError(`Failed to retrieve companies watchlist:  ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return EMPTY;
      })
    );
  }

  exportWatchlistsCompany$(): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.url}/export-company-watchlist`, { responseType: 'blob', observe: 'response' }).pipe(
      catchError(error => {
        parseErrorMessageFromBlob(error).then(errorMessage =>
          this._messageService.showError(`Failed to export company watchlist: ${errorMessage}.`)
        );
        return EMPTY;
      })
    );
  }

  getWatchlistsProject$(): Observable<WatchlistProject[]> {
    return this.http.get<WatchlistProject[]>(this.url + '/project-watchlist').pipe(
      map(projects =>
        projects.map(project => ({
          ...project,
          sensitivityName: transformSensitivity(project.esgRiskScore),
          updatedDate: transformDateString(project.updatedDate),
          activeFrom: transformDateString(project.activeFrom),
        }))
      ),
      catchError(error => {
        this._messageService.showError(`Failed to retrieve projects watchlist:  ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return EMPTY;
      })
    );
  }

  exportWatchlistsProject$(): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.url}/export-project-watchlist`, { responseType: 'blob', observe: 'response' }).pipe(
      catchError(error => {
        parseErrorMessageFromBlob(error).then(errorMessage =>
          this._messageService.showError(`Failed to export project watchlist: ${errorMessage}.`)
        );
        return EMPTY;
      })
    );
  }

  getWatchlistsCountrySector$(isCritical: boolean): Observable<WatchListCountrySectorFlattenedResponse> {
    const params = { isCritical };

    return this.http.get<WatchlistCountrySectorResponse>(this.url + '/country-industry-watchlist', { params }).pipe(
      map(response => ({
        industrySegmentHeaders: response.industrySegmentHeaders,
        countrySectorData: response.countrySectorWatchlistTableContentDtos.map(this.transformCountryData),
      })),
      catchError(error => {
        this._messageService.showError(`Failed to retrieve country/sector watchlist:  ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return EMPTY;
      })
    );
  }

  exportWatchlistsCountrySector$(isCritical: boolean): Observable<HttpResponse<Blob>> {
    const params = { isCritical };

    return this.http.get(`${this.url}/export-country-industry-watchlist`, { params, responseType: 'blob', observe: 'response' }).pipe(
      catchError(error => {
        parseErrorMessageFromBlob(error).then(errorMessage =>
          this._messageService.showError(`Failed to export country sector watchlist: ${errorMessage}.`)
        );
        return EMPTY;
      })
    );
  }

  transformCountryData = (dto: CountrySectorWatchlistTableContentDto): FlattenedCountrySector => {
    return {
      countryName: dto.countryName,
      ...Object.fromEntries(dto.countrySectorData.map(s => [s.sectorName, s])),
    } as FlattenedCountrySector;
  };
}
